// Tiny DOM helper, no framework. Just enough to build forms/panels.
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const child of children) {
    node.append(child);
  }
  return node;
}

export function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = el("a", { href: url, download: filename });
  document.body.append(a);
  a.click();
  a.remove();
  // Delay revoke a bit - doing it immediately can race the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// Uses a real anchor click (target=_blank) instead of window.open() -
// Chromium blocks scripted window.open navigation to a blob: URL, but a
// direct anchor click still works.
//
// Auto-revokes after a bounded delay. The caller also revokes on its next
// View click, but that only covers the same panel instance staying alive -
// switching tabs loses that reference. Without this, decrypted plaintext
// stays reachable via the URL for the rest of the session. 10 minutes is
// generous reading time without leaving it open indefinitely.
const VIEW_URL_TTL_MS = 10 * 60 * 1000;

export function triggerView(blob: Blob): string {
  const url = URL.createObjectURL(blob);
  const a = el("a", { href: url, target: "_blank", rel: "noopener" });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), VIEW_URL_TTL_MS);
  return url;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
